import * as React from 'react'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useTranslation } from "react-i18next"
import { Tooltip } from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import TablePagination from "@mui/material/TablePagination";
import { t } from 'i18next'
import dayjs from 'dayjs'
import { useState, useMemo } from 'react'
import { visuallyHidden } from '@mui/utils';
import Swal from 'sweetalert2'
import axios from '../../lib/axios'
import { useStateContext } from '../../context/ContextProvider'
import { isValid, formatQuantity } from '../../helpers/helper'

const rowCalculatedForecastQty = (item) => isValid(item.calculated_forecast_qty) ? item.calculated_forecast_qty : 0

const rowFirmOrderQty = (item) => isValid(item.firm_order_qty) ? item.firm_order_qty : 0

const rowFinalForecastQty = (item) => {
    if (isValid(item.manual_override_qty)) {
        return Number(item.manual_override_qty)
    }

    return rowCalculatedForecastQty(item)
}

const rowRemainingForecastQty = (item) => {
    return Math.max(0, rowFinalForecastQty(item) - rowFirmOrderQty(item))
}

const totalFinalForecastQty = (row) => (row.forecast_items ?? []).reduce((total, item) => total + rowFinalForecastQty(item), 0)

const totalFirmOrderQty = (row) => (row.forecast_items ?? []).reduce((total, item) => total + rowFirmOrderQty(item), 0)

const totalRemainingForecastQty = (row) => (row.forecast_items ?? []).reduce((total, item) => total + rowRemainingForecastQty(item), 0)

const getSortValue = (row, property) => {
    switch (property) {
        case 'forecast_no': return row.forecast_number ?? ''
        case 'customer_name': return row.customer_name ?? ''
        case 'site_name': return row.site_name ?? ''
        case 'period_from': return row.period_from ?? ''
        case 'period_to': return row.period_to ?? ''
        case 'calculation_basis': return row.calculation_basis_name ?? ''
        case 'final_forecast_qty': return totalFinalForecastQty(row)
        case 'firm_order_qty': return totalFirmOrderQty(row)
        case 'remaining_forecast_qty': return totalRemainingForecastQty(row)
        default: return ''
    }
}

const compareSortValues = (a, b) => {
    if (!isValid(a)) {
        return isValid(b) ? -1 : 0
    }
    if (!isValid(b)) {
        return 1
    }
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b
    }
    return String(a).localeCompare(String(b))
}

function Row(props) {
    const { row, onDeleted } = props;
    const [open, setOpen] = React.useState(false)
    const { t } = useTranslation()

    const forecastItems = row.forecast_items ?? []

    const rowTotalFinalForecastQty = totalFinalForecastQty(row)
    const rowTotalFirmOrderQty = totalFirmOrderQty(row)
    const rowTotalRemainingForecastQty = totalRemainingForecastQty(row)

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset !important' } }}>
                <TableCell sx={{ padding: '8px 16px', fontWeight: '500' }}>{row.forecast_number}</TableCell>
                <TableCell sx={{ padding: '8px 16px', fontStyle: 'italic' }}>{row.customer_name}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{row.site_name}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{row.period_from ? dayjs(row.period_from).format("YYYY-MM-DD") : ''}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{row.period_to ? dayjs(row.period_to).format("YYYY-MM-DD") : ''}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{row.calculation_basis_name}</TableCell>
                <TableCell sx={{ padding: '8px 16px', fontWeight: '700' }}>{formatQuantity(rowTotalFinalForecastQty)}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{formatQuantity(rowTotalFirmOrderQty)}</TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>{formatQuantity(rowTotalRemainingForecastQty)}</TableCell>
                <TableCell sx={{ padding: '8px 5px' }}>
                    <ForecastActions id={row.id} forecastNo={row.forecast_number} onDeleted={onDeleted} />
                </TableCell>
                <TableCell sx={{ padding: '8px 16px' }}>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell style={{ padding: 0 }} colSpan={11}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box>
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table size="small" aria-label="forecast-items">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('item')}</TableCell>
                                            <TableCell>{t('calculated_forecast_qty')}</TableCell>
                                            <TableCell>{t('manual_override_qty')}</TableCell>
                                            <TableCell>{t('final_forecast_qty')}</TableCell>
                                            <TableCell>{t('firm_order_qty')}</TableCell>
                                            <TableCell>{t('remaining_forecast_qty')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {forecastItems.map((item, idx) => (
                                            <TableRow key={item.id ?? idx}>
                                                <TableCell sx={{ padding: '8px 16px' }}>{item.item_no}{item.description ? ` - ${item.description}` : ''}</TableCell>
                                                <TableCell sx={{ padding: '8px 16px' }}>{formatQuantity(rowCalculatedForecastQty(item))}</TableCell>
                                                <TableCell sx={{ padding: '8px 16px' }}>{isValid(item.manual_override_qty) ? formatQuantity(item.manual_override_qty) : '-'}</TableCell>
                                                <TableCell sx={{ padding: '8px 16px' }}>{formatQuantity(rowFinalForecastQty(item))}</TableCell>
                                                <TableCell sx={{ padding: '8px 16px' }}>{formatQuantity(rowFirmOrderQty(item))}</TableCell>
                                                <TableCell sx={{ padding: '8px 16px' }}>{formatQuantity(rowRemainingForecastQty(item))}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>

        </React.Fragment>
    );
}

const ForecastActions = ({ id, forecastNo, onDeleted }) => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { config } = useStateContext()

    const deleteForecast = async () => {
        const isConfirm = await Swal.fire({
            title: t('title_delete') + t('del_forecast') + (forecastNo ?? id) + "?",
            text: t('text_delete'),
            icon: 'warning',
            customClass: 'error',
            showCloseButton: true,
            iconColor: '#FF0000',
            reverseButtons: true,
            showCancelButton: true,

            confirmButtonText: t('confirmButtonText_delete'),
            cancelButtonText: t('cancel')
        }).then((result) => result.isConfirmed)

        if (!isConfirm) {
            return
        }

        await axios.delete(`/api/forecasts/${id}`, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })

            if (onDeleted) {
                onDeleted()
            }
        }).catch(({ response }) => {
            Swal.fire({
                text: response?.data?.message,
                icon: "error",
                customClass: 'error',
                showCloseButton: true,
                iconColor: '#FF0000'
            })
        })
    }

    return (
        <div className='flex gap-1'>
            <Tooltip disableInteractive title={t('view')} placement='bottom'>
                <div style={{ color: 'rgba(0,0,0,.54)' }}>
                    <button onClick={() => navigate(`${id}/view`, { state: { prevPathname: location.pathname } })}><span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"><i className="fa-solid fa-eye"></i></span></button>
                </div>
            </Tooltip>
            <Tooltip disableInteractive title={t('update')} placement='bottom'>
                <div style={{ color: 'rgba(0,0,0,.54)' }}>
                    <button onClick={() => navigate(`${id}`, { state: { prevPathname: location.pathname } })}><span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"><i className="fa-solid fa-pencil"></i></span></button>
                </div>
            </Tooltip>
            <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                <div style={{ color: 'rgba(0,0,0,.54)' }}>
                    <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={deleteForecast}><i className="fa-solid fa-trash"></i></span>
                </div>
            </Tooltip>
        </div>
    )
}

const SortableTableHeaderRow = (props) => {
    const { order, orderBy, onRequestSort } = props
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property)
    }

    const columns = [
        { id: 'forecast_no', label: t('forecast_no') },
        { id: 'customer_name', label: t('customer') },
        { id: 'site_name', label: t('site') },
        { id: 'period_from', label: t('period_from') },
        { id: 'period_to', label: t('period_to') },
        { id: 'calculation_basis', label: t('calculation_basis') },
        { id: 'final_forecast_qty', label: t('final_forecast_qty') },
        { id: 'firm_order_qty', label: t('firm_order_qty') },
        { id: 'remaining_forecast_qty', label: t('remaining_forecast_qty') },
    ]

    return (
        <TableHead>
            <TableRow>
                {columns.map((column) => (
                    <TableCell key={column.id} sortDirection={orderBy === column.id ? order : false}>
                        <TableSortLabel sx={{ color: '#88909C !important', fontWeight: '400' }}
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={createSortHandler(column.id)}>
                            {column.label}
                            {orderBy === column.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell sx={{ color: '#88909C !important', fontWeight: '400' }}>{t('actions')}</TableCell>
                <TableCell />
            </TableRow>
        </TableHead>
    )
}

export default function CollapsibleTable({ items, total, page = 1, rowsPerPage = 10, opc = undefined, orppc = undefined, onDeleted = undefined }) {

    const { t } = useTranslation()

    const pg = Number(page - 1)
    const rpg = Number(rowsPerPage)

    const [orderBy, setOrderBy] = useState('forecast_no')
    const [order, setOrder] = useState('desc')

    function handleChangePage(event, newpage) {
        if (opc) {
            opc(Number(newpage + 1));
        }
    }

    function handleChangeRowsPerPage(event) {
        if (orppc) {
            opc(1);
            orppc(parseInt(event.target.value, 10));
        }
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const sortedItems = useMemo(() => {
        const sorted = [...items].sort((a, b) => compareSortValues(getSortValue(a, orderBy), getSortValue(b, orderBy)))
        return order === 'desc' ? sorted.reverse() : sorted
    }, [items, order, orderBy])

    return (
        <div>
            <div style={{ width: '100%', mb: 2, border: 'none' }}>
                <TableContainer>
                    <Table aria-label="collapsible table" stickyHeader>
                        <colgroup>
                            <col width="9%" />
                            <col width="13%" />
                            <col width="10%" />
                            <col width="10%" />
                            <col width="10%" />
                            <col width="11%" />
                            <col width="10%" />
                            <col width="9%" />
                            <col width="10%" />
                            <col width="8%" />
                            <col />
                        </colgroup>
                        <SortableTableHeaderRow
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                        />
                        <TableBody>
                            {sortedItems.length <= 0 ? <TableRow><TableCell colSpan={11} sx={{ textAlign: 'center' }}>{t('no_data')}</TableCell></TableRow> : null}
                            {sortedItems.map((row) => (
                                <Row key={row.id} row={row} onDeleted={onDeleted} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    className='table-pagination'
                    labelRowsPerPage={t('rowsPerPage')}
                    rowsPerPageOptions={[10, 15]}
                    component="div"
                    count={total}
                    rowsPerPage={rpg}
                    page={pg}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </div>
        </div>
    );
}
